/**
 * useArAging — A/R aging for the company home. Single fetch shared by the
 * verdict engine (needs the aged-A/R signal) and ArAgingCenter (display), so
 * the home doesn't fetch aging twice. Amounts are DOLLARS-numbers (api shape).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isInLocalMode } from '@/lib/api';
import { useAppStore } from '@/stores/useAppStore';
import type { AgingReport } from '@/types';

export function useArAging(enabled = true) {
  const { activeEntity } = useAppStore();
  const [data, setData] = useState<AgingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const cancelRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || isInLocalMode() || !activeEntity?.id) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const r = (await api.getAgingReport()) as AgingReport;
      if (!cancelRef.current) setData(r);
    } catch {
      if (!cancelRef.current) setError(true);
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [activeEntity?.id, enabled]);

  useEffect(() => {
    cancelRef.current = false;
    refresh();
    return () => {
      cancelRef.current = true;
    };
  }, [refresh]);

  /** Aged A/R (61+ days = days90 + over90) in CENTS, or null if unknown. */
  const agedArCents =
    data && data.totals
      ? Math.round(((data.totals.days90 ?? 0) + (data.totals.over90 ?? 0)) * 100)
      : null;

  return { data, agedArCents, loading, error, refresh };
}
